import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, User, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Footer from '../components/Footer';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signup(email, password, name);
      navigate('/subscription');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col"
      style={{
        background: 'linear-gradient(140deg, #08101e 0%, #0f172a 55%, #0b3b4b 100%)'
      }}
    >
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
        <div
          className="backdrop-blur-xl rounded-2xl p-8 border"
          style={{
            background: 'rgba(var(--bg-elev-1), 0.8)',
            borderColor: 'rgba(var(--line), 0.6)'
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <h1
              className="text-3xl font-bold mb-2"
              style={{
                background: 'linear-gradient(135deg, var(--accent-cyan) 0%, var(--accent-ocean) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Inscription
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              Créez votre compte Coach Life
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2"
            >
              <AlertCircle size={18} className="text-red-400" />
              <span className="text-sm text-red-400">{error}</span>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Nom
              </label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border bg-transparent focus:outline-none focus:ring-2"
                  style={{
                    borderColor: 'var(--line)',
                    color: 'var(--text-primary)',
                    '--tw-ring-color': 'var(--accent-cyan)'
                  }}
                  placeholder="Votre nom"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Email
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg border bg-transparent focus:outline-none focus:ring-2"
                  style={{
                    borderColor: 'var(--line)',
                    color: 'var(--text-primary)',
                    '--tw-ring-color': 'var(--accent-cyan)'
                  }}
                  placeholder="votre@email.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Mot de passe
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border bg-transparent focus:outline-none focus:ring-2"
                  style={{
                    borderColor: 'var(--line)',
                    color: 'var(--text-primary)',
                    '--tw-ring-color': 'var(--accent-cyan)'
                  }}
                  placeholder="••••••••"
                />
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Minimum 6 caractères
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-medium transition-all"
              style={{
                background: loading ? 'var(--text-muted)' : 'var(--accent-cyan)',
                color: '#0b1220',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Inscription...' : 'S\'inscrire'}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center space-y-2">
            <p style={{ color: 'var(--text-secondary)' }} className="text-sm">
              Déjà un compte?{' '}
              <Link
                to="/login"
                style={{ color: 'var(--accent-cyan)' }}
                className="font-medium hover:underline"
              >
                Se connecter
              </Link>
            </p>
            <p style={{ color: 'var(--text-muted)' }} className="text-xs">
              <Link
                to="/guide"
                className="hover:underline"
                style={{ color: 'var(--accent-ocean)' }}
              >
                📖 Guide d'utilisation
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default Signup;
